import React, { useCallback, useRef } from 'react'
import { createUseStyles } from 'react-jss'
import Flickity from 'react-flickity-component'
import gsap from 'gsap'
import { LinkWithCursorHoverEffect } from '../Link'
import isEmpty from 'lodash/isEmpty'
import theme from '../../style/theme'
import { span, sliceMarginStyles } from '../../style/span'
import { useDragCursor } from '../Cursor'
import Heading from '../Heading'
import cn from 'classnames'

const preventVerticalScrolling = (flickity) => {
  // This will prevent the vertical scrolling when scrolling in flickity
  flickity.on('dragStart', () => { document.ontouchmove = e => e.preventDefault() })
  flickity.on('dragEnd', () => { document.ontouchmove = () => true })
}

const FLICKITY_OPTIONS = {
  cellAlign: 'left',
  pageDots: false,
  contain: true,
  freeScroll: true,
  prevNextButtons: true,
  adaptiveHeight: false,
  groupCells: true,
  wrapAround: false,
  selectedAttraction: 0.015,
  friction: 0.25,
  percentPosition: true,
  autoPlay: 0
}

/**
 * RecipeSlider component - specialized slider for recipe displays
 * 2-column layout: title on left (1/4), slider on right (3/4)
 */
const RecipeSlider = ({ className, children, title, description, link, flickityOptions = FLICKITY_OPTIONS, onChange, ...rest }) => {
  const classes = useStyles()
  const containerRef = useRef()
  const setFlickityRef = useCallback((flickity) => {
    if (flickity) {
      gsap.set(flickity.element, { display: 'block', overflow: 'visible' })
      // Flickity consumes the mouse move events so we need to create a customer event so the custom mouse
      // cursor continues to follow the mouse cursor
      flickity.on('dragMove', (event, pointer, moveVector) => {
        const dragMoveEvent = new window.CustomEvent('flickity-dragMove', { detail: event })
        window.dispatchEvent(dragMoveEvent)
      })
      preventVerticalScrolling(flickity)

      if (onChange) {
        flickity.on('change', onChange)
      }
    }
  }, [])

  useDragCursor(containerRef)

  return (
    <section className={cn(className, classes.section)}>
      {/* Mobile layout - stacked */}
      <div className={classes.mobileLayout}>
        {title && <Heading text={title} className={classes.mobileTitle} />}
        {description && <p className={classes.mobileDescription}>{description}</p>}
        {!isEmpty(link) && (
          <LinkWithCursorHoverEffect link={link} className={classes.mobileLink}>
            More Recipes
          </LinkWithCursorHoverEffect>
        )}

        <div ref={containerRef}>
          <Flickity
            options={flickityOptions}
            disableImagesLoaded
            reloadOnUpdate
            static
            className={classes.flickity}
            flickityRef={setFlickityRef}
            {...rest}
          >
            {children}
          </Flickity>
        </div>
      </div>      {/* Desktop layout - Title full width, then 2 columns */}
      <div className={classes.desktopLayout}>
        {/* Full width title at top */}
        {title && (
          <div className={classes.fullWidthTitleContainer}>
            <Heading key={title} text={title} className={classes.desktopTitle} />
          </div>
        )}
        {/* Two column layout below title */}
        <div className={classes.twoColumnContainer}>          {/* Left column - Link and Description (1/4 width) */}
          <div className={classes.leftColumn}>
            {description && <p className={classes.desktopDescription}>{description}</p>}
            {!isEmpty(link) && (
              <LinkWithCursorHoverEffect link={link} className={classes.desktopLink}>
                More Recipes
              </LinkWithCursorHoverEffect>
            )}
          </div>

          {/* Right column - Slider (3/4 width) */}
          <div className={classes.sliderColumn}>
            <div ref={containerRef}>
              <Flickity
                options={flickityOptions}
                disableImagesLoaded
                reloadOnUpdate
                static
                className={classes.flickity}
                flickityRef={setFlickityRef}
                {...rest}
              >
                {children}
              </Flickity>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const useStyles = createUseStyles({
  section: {
    extend: [sliceMarginStyles],
    padding: [0, span(1)],
    overflowX: 'hidden',
    [theme.breakpoints.up('md')]: {
      padding: [0, span(1, 'md')]
    }
  },

  // Mobile layout - stacked vertically
  mobileLayout: {
    display: 'block',
    [theme.breakpoints.up('lg')]: {
      display: 'none'
    }
  },
  // Desktop layout - Title full width, then 2 columns
  desktopLayout: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'block'
    }
  },

  // Full width title container
  fullWidthTitleContainer: {
    [theme.breakpoints.up('lg')]: {
      width: '100%',
      marginBottom: '60px'
    }
  },

  // Two column container below title
  twoColumnContainer: {
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '40px',
      minHeight: '400px'
    }
  },

  // Left column - Link and Description (1/4 width)
  leftColumn: {
    [theme.breakpoints.up('lg')]: {
      width: '20%',
      flexShrink: 0,
      paddingRight: '40px'
    }
  },

  // Right column - Slider (3/4 width)
  sliderColumn: {
    [theme.breakpoints.up('lg')]: {
      width: '80%',
      flexShrink: 0,
      overflow: 'hidden'
    }
  },

  flickity: {
    outline: 'none',
    whiteSpace: 'nowrap',
    display: 'flex',
    padding: [0, 0],
    '& > *': {
      flexShrink: 0
    },
    // Improved navigation buttons for recipe slider
    '& .flickity-prev-next-button': {
      top: '40%',
      transform: 'translateY(-50%)',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 1)'
      }
    }
  },

  // Mobile styles
  mobileTitle: {
    whiteSpace: 'pre-wrap',
    display: 'block',
    lineHeight: 1.1,
    marginBottom: span(1),
    color: theme.colors.primary,
    fontSize: '30px',
    fontWeight: 'bold',
    fontFamily: theme.fonts.headings
  },

  mobileDescription: {
    fontSize: '16px',
    lineHeight: 1.4,
    color: theme.colors.primary,
    marginBottom: span(1),
    fontFamily: theme.fonts.body
  },

  mobileLink: {
    display: 'inline-block',
    textTransform: 'uppercase',
    textDecoration: 'none',
    position: 'relative',
    margin: [0, 0, span(1), 0],
    ...theme.link()
  },
  // Desktop title styles - now full width at top
  desktopTitle: {
    whiteSpace: 'normal',
    display: 'block',
    color: theme.colors.primary,
    fontFamily: theme.fonts.headings,
    fontSize: '48px',
    textAlign: 'left',
    lineHeight: 1.3,
    marginBottom: 0, // Remove bottom margin since it's now in fullWidthTitleContainer
    [theme.breakpoints.up('xl')]: {
      fontSize: '48px'
    }
  },

  desktopDescription: {
    fontSize: '14px',
    lineHeight: '21px',
    color: '#031F1C',
    fontFamily: theme.fonts.body,
    marginBottom: '4px !important'
  },

  desktopLink: {
    display: 'inline-block',
    textTransform: 'uppercase',
    textDecoration: 'none',
    position: 'relative',
    ...theme.link(),
    fontSize: '14px',
    marginTop: span(0.5, 'md')
  }
}, { name: 'RecipeSlider' })

export default RecipeSlider
